import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, ArrowUp, Loader2 } from "lucide-react";
import { useLang } from "../../lib/LangContext";
import { useNearSections } from "../../lib/useNearSections";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/assistant`;
const SESSION_KEY = "puregloss_assistant_session";

const COPY = {
  de: { open: "Fragen? KI-Assistent", title: "PUREGLOSS Assistent", hello: "Guten Tag! Ich beantworte Fragen zu unseren Leistungen, Einsatzgebiet und Terminen.", placeholder: "Ihre Frage…", note: "KI-Assistent · Antworten können ungenau sein. Bitte keine sensiblen Daten eingeben.", error: "Der Assistent ist gerade nicht erreichbar. Schreiben Sie uns gern per WhatsApp.", suggestions: ["Kommen Sie auch nach Bonn?", "Wie bekomme ich ein Angebot?", "Reinigen Sie auch Sofas?"] },
  ua: { open: "Питання? AI-асистент", title: "Асистент PUREGLOSS", hello: "Вітаю! Відповім на питання про наші послуги, райони виїзду та запис.", placeholder: "Ваше питання…", note: "AI-асистент · Відповіді можуть бути неточними. Не вводьте чутливі дані.", error: "Асистент зараз недоступний. Напишіть нам у WhatsApp.", suggestions: ["Ви приїжджаєте в Bonn?", "Як отримати пропозицію?", "Ви чистите дивани?"] },
  en: { open: "Questions? AI assistant", title: "PUREGLOSS Assistant", hello: "Hello! I can answer questions about our services, service area and appointments.", placeholder: "Your question…", note: "AI assistant · Answers may be inaccurate. Please don't enter sensitive data.", error: "The assistant is unavailable right now. Feel free to message us on WhatsApp.", suggestions: ["Do you come to Bonn?", "How do I get a quote?", "Do you clean sofas?"] },
  pl: { open: "Pytania? Asystent AI", title: "Asystent PUREGLOSS", hello: "Dzień dobry! Odpowiem na pytania o nasze usługi, obszar dojazdu i terminy.", placeholder: "Twoje pytanie…", note: "Asystent AI · Odpowiedzi mogą być niedokładne. Nie podawaj danych wrażliwych.", error: "Asystent jest teraz niedostępny. Napisz do nas na WhatsApp.", suggestions: ["Czy dojeżdżacie do Bonn?", "Jak otrzymać ofertę?", "Czy pierzecie kanapy?"] },
  ru: { open: "Вопросы? AI-ассистент", title: "Ассистент PUREGLOSS", hello: "Здравствуйте! Отвечу на вопросы о наших услугах, районах выезда и записи.", placeholder: "Ваш вопрос…", note: "AI-ассистент · Ответы могут быть неточными. Не вводите чувствительные данные.", error: "Ассистент сейчас недоступен. Напишите нам в WhatsApp.", suggestions: ["Вы приезжаете в Bonn?", "Как получить предложение?", "Вы чистите диваны?"] },
};

const sessionId = () => {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return `${Date.now()}-tmp-session`;
  }
};

// Reads the SSE stream and calls onDelta for every text chunk.
const streamReply = async (body, onDelta) => {
  const res = await fetch(`${API}/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!res.ok || !res.body) {
    const err = new Error(String(res.status));
    if (res.status === 429) err.detail = (await res.json().catch(() => ({}))).detail;
    throw err;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const events = buf.split("\n\n");
    buf = events.pop();
    for (const ev of events) {
      if (!ev.startsWith("data: ")) continue;
      const data = JSON.parse(ev.slice(6));
      if (data.error) throw new Error(data.error);
      if (data.delta) onDelta(data.delta);
    }
  }
};

const Bubble = ({ role, children }) => (
  <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}>
    <div data-testid={`assistant-msg-${role}`} className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${role === "user" ? "bg-gold text-black" : "bg-white/[0.06] text-zinc-100 border border-white/10"}`}>
      {children}
    </div>
  </div>
);

export const AssistantChat = () => {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.de;
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const listRef = useRef(null);
  // On phones the launcher waits until the hero is scrolled past (keeps hero CTAs clear).
  const [pastHero, setPastHero] = useState(false);
  const near = useNearSections();

  useEffect(() => {
    const onScroll = () => setPastHero(window.scrollY > window.innerHeight * 0.8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open || loaded) return;
    setLoaded(true);
    fetch(`${API}/history?session_id=${encodeURIComponent(sessionId())}`)
      .then((r) => (r.ok ? r.json() : { messages: [] }))
      .then((d) => setMessages((m) => (m.length ? m : d.messages || [])))
      .catch(() => {});
  }, [open, loaded]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages, open]);

  useEffect(() => {
    document.body.dataset.assistantOpen = open ? "1" : "0";
  }, [open]);

  if (pathname === "/app" || pathname.startsWith("/app/")) return null;

  const send = async (text) => {
    const msg = text.trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    const append = (delta) => setMessages((m) => {
      const next = [...m];
      const last = next[next.length - 1];
      if (!last || last.role !== "assistant") return [...next, { role: "assistant", content: delta }];
      next[next.length - 1] = { role: "assistant", content: last.content + delta };
      return next;
    });
    let failure = "";
    try {
      await streamReply({ session_id: sessionId(), message: msg, lang }, append);
    } catch (e) {
      failure = e?.detail || c.error;
    }
    setMessages((m) => {
      const next = [...m];
      const last = next[next.length - 1];
      if (failure || !last?.content) next[next.length - 1] = { role: "assistant", content: failure || c.error };
      return next;
    });
    setBusy(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid="assistant-launcher"
        onClick={() => setOpen(true)}
        aria-label={c.open}
        className={`fixed z-40 right-3 bottom-[76px] md:right-auto md:left-6 md:bottom-6 ${near ? "hidden" : pastHero ? "inline-flex" : "hidden md:inline-flex"} items-center justify-center gap-2.5 w-11 h-11 md:w-auto md:h-auto rounded-full md:rounded-none bg-[#0A0A0C]/90 backdrop-blur border border-gold/40 text-white md:px-4 md:py-3 text-[11px] tracking-[0.2em] uppercase hover:border-gold transition-colors duration-300`}
      >
        <MessageCircle size={15} className="text-gold" />
        <span className="hidden md:inline">{c.open}</span>
      </button>
    );
  }

  return (
    <div data-testid="assistant-panel" role="dialog" aria-label={c.title} className="fixed z-50 inset-x-3 bottom-3 md:inset-x-auto md:left-6 md:bottom-6 md:w-[380px] h-[70vh] md:h-[560px] flex flex-col bg-[#0A0A0C] border border-white/10 shadow-2xl shadow-black/70">
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <div className="font-heading text-white tracking-tight">{c.title}</div>
        <button type="button" data-testid="assistant-close" onClick={() => setOpen(false)} aria-label="Close" className="text-zinc-400 hover:text-gold"><X size={18} /></button>
      </div>
      <div ref={listRef} data-testid="assistant-messages" className="flex-1 overflow-y-auto px-4 py-5 space-y-3">
        <Bubble role="assistant">{c.hello}</Bubble>
        {messages.map((m, i) => (
          <Bubble key={i} role={m.role}>{m.content || <Loader2 size={14} className="animate-spin text-gold" />}</Bubble>
        ))}
        {messages.length === 0 && (
          <div className="pt-2 flex flex-wrap gap-2">
            {c.suggestions.map((s) => (
              <button key={s} type="button" data-testid="assistant-suggestion" onClick={() => send(s)} className="border border-white/15 text-zinc-300 px-3 py-1.5 text-xs hover:border-gold hover:text-gold transition-colors">{s}</button>
            ))}
          </div>
        )}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="border-t border-white/10 p-3 flex gap-2">
        <input
          data-testid="assistant-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          maxLength={800}
          placeholder={c.placeholder}
          className="flex-1 bg-black/40 border border-white/15 px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-gold"
        />
        <button type="submit" data-testid="assistant-send" disabled={busy || !input.trim()} aria-label="Send" className="bg-gold text-black px-3.5 disabled:opacity-40">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowUp size={16} />}
        </button>
      </form>
      <p className="px-4 pb-3 text-[10px] text-zinc-500 leading-snug">{c.note}</p>
    </div>
  );
};
